import React, { FC, Fragment } from 'react';
import { formatDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { Box, Typography, Button } from '@mui/material';
import { InvoiceStatus } from 'models/invoice';
import DownloadIcon from '@mui/icons-material/Download';

type InvoiceInfoProps = {
    order: Order;
};
const InvoiceInfo: FC<InvoiceInfoProps> = ({ order }: InvoiceInfoProps) => {
    const { t: translate } = useTranslation(['orders', 'invoices']);

    const colorMap = {
        [InvoiceStatus.ALL.valueOf()]: '', // Ignore this, just to solve type issues
        [InvoiceStatus.CREATED.valueOf()]: '#444',
        [InvoiceStatus.SCHEDULED.valueOf()]: 'warning.main',
        [InvoiceStatus.PROCESSING.valueOf()]: 'warning.main',
        [InvoiceStatus.SENT.valueOf()]: 'success.main',
        [InvoiceStatus.ERROR.valueOf()]: 'error.main',
        [InvoiceStatus.EXTERNAL_INVOICE_ERROR.valueOf()]: 'warning.main',
        [InvoiceStatus.VOIDED.valueOf()]: 'error.main',
    };

    return (
        <Fragment>
            <Box mb={1}>
                <Typography variant="subtitle2">{translate('invoices:INVOICE_STATUS')}</Typography>
                <Box>
                    <Typography variant="body1" sx={{ color: colorMap[order.invoice.status] }}>
                        {translate(`invoices:${order.invoice.status}`)}
                    </Typography>
                </Box>
            </Box>

            <Box mb={1}>
                <Typography variant="subtitle2">{translate('invoices:INVOICE_NUMBER')}</Typography>
                <Box>{order.invoice.invoiceNumber}</Box>
            </Box>
            {order.invoiceCreatedTime ? (
                <Box mb={1}>
                    <Typography variant="subtitle2">{translate('invoices:INVOICE_DATE')}</Typography>
                    <Box>{formatDate(order.invoiceCreatedTime, 'date')}</Box>
                </Box>
            ) : undefined}
            {order.invoice.sentDate ? (
                <Box mb={1}>
                    <Typography variant="subtitle2">{translate('invoices:INVOICE_SENT')}</Typography>
                    <Box>{formatDate(order.invoice.sentDate, 'date')}</Box>
                </Box>
            ) : undefined}
            <Box>
                <Button
                    fullWidth
                    target="_blank"
                    rel="noreferrer"
                    href={order.invoice?.pdfUrl}
                    startIcon={<DownloadIcon />}
                    size="small"
                    disabled={!order.invoice?.pdfUrl}
                    data-test="invoice-actions.download-invoice-pdf"
                >
                    {translate('invoices:DOWNLOAD_INVOICE_PDF')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default InvoiceInfo;
